// In-memory intent buffer for "jump to a specific message" navigations.
//
// The activity feed and the search results share the same UX shape:
//   1. The URL changes to the destination channel — and nothing else.
//   2. The MessageFeed for that channel lands directly on the target on its
//      first paint, via the COLD_AROUND_TARGET bootstrap path.
//
// The hard part is that the click site and the MessageFeed mount are not
// in the same React commit for cross-workspace navigations. The shell stays
// mounted, but the old route content unmounts while the new workspace hydrates
// Dexie, resolves the route's RouteWorkspaceProvider, and finally mounts a
// fresh ChatView / MessageFeed. A pure "fire an event and hope someone's
// listening" approach drops the intent in that gap.
//
// We split the difference:
//   - A module-scoped Map remembers the most recent jump per channel for a
//     short TTL. This is what survives the unmount/remount gap so the new
//     MessageFeed can drain it during its channel switch.
//   - A CustomEvent fires alongside the buffer write so a *currently
//     mounted* MessageFeed (same-channel re-target) can react in the same
//     tick, without waiting for a re-mount that will never happen.
//
// We deliberately keep same-origin app navigations out of the URL. Memory keeps
// those jumps invisible while still giving us the same atomic-first-paint
// guarantee. Cross-origin navigations cannot keep module memory, so callers may
// add the one-shot query params this module consumes and strips on the
// destination origin.
//
// External deep-links may also use the one-shot params, but they should remain
// an explicit opt-in. `consumeUrlMessageJump` strips them before bootstrap
// continues, so normal channel URLs stay clean after the first paint.

// Pending jumps go stale after this many ms. The motivation is purely
// defensive: if a user clicks a search result, then immediately navigates
// elsewhere via the sidebar, then navigates back to that same channel
// 30 seconds later, we should NOT replay the original jump — by then it
// is no longer "the thing I just asked for", it is just stale state. The
// happy path consumes the entry in well under a second, so this threshold
// is comfortable.
const STALE_AFTER_MS = 10_000;
const URL_CHANNEL_PARAM = 'jump_channel';
const URL_SORT_ORDER_PARAM = 'jump_sort_order';

// channelId (string) -> { sortOrder, requestedAt }
const pendingByChannel = new Map();

// Subscribe to this on `window` to handle same-channel jumps (the buffer
// path handles cross-channel ones automatically via bootstrap).
export const MESSAGE_JUMP_REQUESTED_EVENT = 'output:internal:message-jump-requested';

function isFresh(entry, now = Date.now()) {
  return Boolean(entry) && now - entry.requestedAt <= STALE_AFTER_MS;
}

// Sweep abandoned entries on each write. Without this, an intent for a
// channel the user never actually navigates to would sit in the map forever
// (consume is the only other code path that drops entries, and it only
// touches the one channel being consumed). The Map is small in practice
// — one entry per channel the user has recently clicked — so an iteration
// on each write is cheaper than any timer-based approach.
function pruneStaleEntries(now = Date.now()) {
  for (const [key, entry] of pendingByChannel) {
    if (now - entry.requestedAt > STALE_AFTER_MS) pendingByChannel.delete(key);
  }
}

// Fire a jump intent for a channel. Writes the buffer and dispatches the
// event in one call so callers don't have to worry about ordering — both
// channels reach their respective consumers.
//
// Callers should call this *before* the navigation that switches to the
// channel. The order matters for the cross-workspace case: by the time
// the new MessageFeed mounts and reads `consumeMessageJump`, the entry
// must already be there.
export function requestMessageJump({ channelId, sortOrder }) {
  if (!channelId || !sortOrder) return;
  const now = Date.now();
  pruneStaleEntries(now);
  const key = String(channelId);
  pendingByChannel.set(key, {
    sortOrder: String(sortOrder),
    requestedAt: now,
  });
  window.dispatchEvent(
    new CustomEvent(MESSAGE_JUMP_REQUESTED_EVENT, {
      detail: { channelId: key, sortOrder: String(sortOrder) },
    }),
  );
}

// Hand back a consumed-but-unfulfilled intent (e.g. a boot superseded before
// its jump landed) WITHOUT re-firing the event: the successor boot or drain
// reads the buffer directly, and an event here could race its window installs.
export function restoreMessageJump({ channelId, sortOrder }) {
  if (!channelId || !sortOrder) return;
  pruneStaleEntries();
  pendingByChannel.set(String(channelId), {
    sortOrder: String(sortOrder),
    requestedAt: Date.now(),
  });
}

// Remove and return the pending jump for a channel, if any. Stale entries
// (older than STALE_AFTER_MS) are dropped on read so callers never have
// to do TTL checks themselves.
//
// Both consumers (the bootstrap branch and the same-channel event handler)
// should call this so the buffer never accumulates uncollected entries.
export function consumeMessageJump(channelId) {
  if (!channelId) return null;
  const key = String(channelId);
  const entry = pendingByChannel.get(key);
  if (!entry) {
    return null;
  }
  pendingByChannel.delete(key);
  return isFresh(entry) ? entry.sortOrder : null;
}

function stripUrlJumpParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete(URL_CHANNEL_PARAM);
  url.searchParams.delete(URL_SORT_ORDER_PARAM);
  const nextSearch = url.searchParams.toString();
  const nextPath = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`;
  window.history.replaceState(window.history.state, '', nextPath);
}

export function appendMessageJumpParams(url, jump) {
  if (!jump?.channelId || !jump?.sortOrder) return url;

  url.searchParams.set(URL_CHANNEL_PARAM, String(jump.channelId));
  url.searchParams.set(URL_SORT_ORDER_PARAM, String(jump.sortOrder));
  return url;
}

export function consumeUrlMessageJump(channelId) {
  if (!channelId) return null;

  const params = new URLSearchParams(window.location.search);
  const requestedChannelId = params.get(URL_CHANNEL_PARAM);
  const sortOrder = params.get(URL_SORT_ORDER_PARAM);
  if (!requestedChannelId && !sortOrder) return null;

  stripUrlJumpParams();
  if (String(requestedChannelId) !== String(channelId) || !sortOrder) return null;

  return String(sortOrder);
}

// Read without consuming. Reserved for tests and diagnostics; the normal
// code paths use `consumeMessageJump` so the buffer self-empties.
export function peekMessageJump(channelId) {
  if (!channelId) return null;
  const entry = pendingByChannel.get(String(channelId));
  return isFresh(entry) ? entry.sortOrder : null;
}
